import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import type { Form } from '../../types/form';
import { resolveFormTheme } from '../../types/form';
import { buildFormThemeCssVars } from './FormThemePreview';
import { FormPreviewForm } from './FormPreviewForm';

interface FormPreviewModalProps {
  isOpen: boolean;
  form: Form;
  onClose: () => void;
  onManageSettings?: () => void;
}

export const FormPreviewModal: React.FC<FormPreviewModalProps> = ({
  isOpen,
  form,
  onClose,
  onManageSettings,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const theme = resolveFormTheme(form.theme);

  const handleScrollTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
                style={buildFormThemeCssVars(theme)}
              >
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
                  <Dialog.Title className="text-sm font-semibold text-gray-900">
                    Modo de visualização
                  </Dialog.Title>
                  <button
                    type="button"
                    title="Fechar"
                    aria-label="Fechar visualização"
                    onClick={onClose}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-2 text-sm text-amber-900">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>
                        Esta é uma visualização. O formulário não está aceitando
                        respostas nesta tela.
                      </span>
                    </div>
                    {onManageSettings ? (
                      <button
                        type="button"
                        onClick={onManageSettings}
                        className="shrink-0 text-sm font-medium text-amber-900 underline hover:text-amber-950"
                      >
                        Gerenciar configurações de publicação
                      </button>
                    ) : null}
                  </div>
                </div>

                <div
                  ref={scrollRef}
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6"
                  style={{ backgroundColor: theme.backgroundColor }}
                >
                  {isOpen ? (
                    <FormPreviewForm
                      key={form.updatedAt ?? form.id}
                      form={form}
                      onScrollTop={handleScrollTop}
                    />
                  ) : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FormPreviewModal;
